import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Clock3,
  Grid2X2,
  Layers3,
  PanelTop,
  Rows3,
  Brackets,
} from "lucide-react";
import API from "../services/api";
import BrandLogo from "../components/BrandLogo";
import { getMarketStatusLabel, getMarketWindowForNow, getSuggestedSession, isMarketClosed } from "../utils/marketStatus";

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT", open_time: "7:05 pm", close_time: "8:05 pm" },
  { id: 2, name: "MADHUR NIGHT", open_time: "8:20 pm", close_time: "10:20 pm" },
  { id: 3, name: "MILAN NIGHT", open_time: "8:50 pm", close_time: "10:50 pm" },
  { id: 4, name: "ANMOL BAZAR", open_time: "9:00 pm", close_time: "11:55 pm" },
  { id: 5, name: "KALYAN NIGHT", open_time: "9:25 pm", close_time: "11:25 pm" },
  { id: 6, name: "RAJDHANI NIGHT", open_time: "9:30 pm", close_time: "11:50 pm" },
  { id: 7, name: "MAIN BAZAR", open_time: "9:45 pm", close_time: "11:55 pm" },
  { id: 8, name: "VANDE BHARAT", open_time: "10:25 pm", close_time: "11:55 pm" },
  { id: 9, name: "KALYAN", open_time: "4:10 pm", close_time: "6:10 pm" },
  { id: 10, name: "RAJDHANI DAY", open_time: "2:00 pm", close_time: "5:00 pm" },
  { id: 11, name: "MILAN DAY", open_time: "2:45 pm", close_time: "4:45 pm" },
  { id: 12, name: "SADAR BAZAR", open_time: "2:15 pm", close_time: "4:15 pm" },
  { id: 13, name: "MADHUR DAY", open_time: "1:25 pm", close_time: "2:25 pm" },
  { id: 14, name: "TIME BAZAR", open_time: "12:45 pm", close_time: "1:45 pm" },
  { id: 15, name: "SRIDEVI", open_time: "11:35 am", close_time: "12:35 pm" },
  { id: 16, name: "MILAN MORNING", open_time: "10:10 am", close_time: "11:11 am" },
  { id: 17, name: "KANTARA", open_time: "10:00 am", close_time: "11:00 am" },
];

const gameTypes = [
  { title: "Single", icon: "1", color: "#10b981", tint: "#eafaf3", path: "single" },
  { title: "Single Bulk", icon: Grid2X2, color: "#14b8a6", tint: "#eafaf8", path: "single-bulk" },
  { title: "Jodi", icon: "12", color: "#ec4899", tint: "#fdf2f8", path: "jodi" },
  { title: "Jodi Bulk", icon: Grid2X2, color: "#f43f5e", tint: "#fff1f2", path: "jodi-bulk" },
  { title: "Red Bracket", icon: Brackets, color: "#ef4444", tint: "#fff1f2", path: "red-bracket" },
  { title: "Single Patti", icon: "123", color: "#8b5cf6", tint: "#f3efff", path: "single-patti" },
  { title: "SP Bulk", icon: Grid2X2, color: "#9b5cf6", tint: "#f5f0ff", path: "sp-bulk" },
  { title: "SP Motor", icon: PanelTop, color: "#d946ef", tint: "#fdf0ff", path: "sp-motor" },
  { title: "Double Patti", icon: "112", color: "#f59e0b", tint: "#fff9e8", path: "double-patti" },
  { title: "DP Bulk", icon: Layers3, color: "#f97316", tint: "#fff4ec", path: "dp-bulk" },
  { title: "DP Motor", icon: Rows3, color: "#ef4444", tint: "#fff1f1", path: "dp-motor" },
  { title: "Triple Patti", icon: "777", color: "#64748b", tint: "#f3f6f9", path: "triple-patti" },
  { title: "Half Sangam", icon: "H", color: "#3b82f6", tint: "#eff6ff", path: "half-sangam" },
  { title: "Full Sangam", icon: "F", color: "#6366f1", tint: "#eef2ff", path: "full-sangam" },
];

const closeSessionHiddenPaths = new Set(["jodi", "jodi-bulk", "red-bracket", "half-sangam", "full-sangam"]);

function Bet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const mainRef = useRef(null);
  const selectedMarketId = id || sessionStorage.getItem("selectedMarketId") || "1";

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setMarkets(rows.filter((market) => (market.market_type || "normal") !== "starline"));
      })
      .catch(() => setMarkets([]));
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const resetScroll = () => {
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
    };

    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [selectedMarketId]);

  const market = useMemo(() => {
    const allMarkets = markets.length ? markets : fallbackMarkets;
    return (
      allMarkets.find((item) => String(item.id) === String(selectedMarketId)) ||
      fallbackMarkets.find((item) => String(item.id) === String(selectedMarketId)) ||
      fallbackMarkets[0]
    );
  }, [selectedMarketId, markets]);

  const marketWindow = getMarketWindowForNow(market);
  const openTime = marketWindow.open_time || market.open_time || market.open || market.start_time || "7:05 pm";
  const closeTime = marketWindow.close_time || market.close_time || market.close || market.end_time || "8:05 pm";
  const marketClosed = isMarketClosed(market);
  const isDesktop = viewportWidth >= 900;
  const suggestedSession = getSuggestedSession(market);
  const visibleGameTypes = suggestedSession === "CLOSE"
    ? gameTypes.filter((game) => !closeSessionHiddenPaths.has(game.path))
    : gameTypes;

  const openGame = (game) => {
    if (marketClosed) {
      alert("Market closed for today");
      return;
    }

    sessionStorage.setItem("selectedMarketId", String(market.id || selectedMarketId));
    navigate(`/${game.path}`);
  };

  return (
    <div style={page}>
      <header style={{ ...topBar, ...(isDesktop ? topBarDesktop : null) }}>
        <button type="button" style={backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={30} />
        </button>
        <div style={brand}><BrandLogo size="sm" /></div>
      </header>

      <main ref={mainRef} style={{ ...main, ...(isDesktop ? mainDesktop : null) }}>
        <section style={{ ...marketHeader, ...(isDesktop ? marketHeaderDesktop : null) }}>
          <div style={marketInfoBlock}>
            <span style={marketTitle}>{market.name || "SRIDEVI NIGHT"}</span>
            <span style={timeText}>
              <Clock3 size={12} /> {openTime} - {closeTime}
            </span>
          </div>
          <span style={marketClosed ? closedBadge : openBadge}>{getMarketStatusLabel(market)}</span>
        </section>

        <section style={{ ...gameGrid, ...(isDesktop ? gameGridDesktop : null) }}>
          {visibleGameTypes.map((game) => {
            const Icon = game.icon;

            return (
              <button
                type="button"
                key={game.title}
                style={marketClosed ? { ...disabledGameCard, ...(isDesktop ? gameCardDesktop : null), borderBottomColor: "#cbd5e1" } : { ...gameCard, ...(isDesktop ? gameCardDesktop : null), borderBottomColor: game.color }}
                onClick={() => openGame(game)}
              >
                <span style={marketClosed ? disabledIconBox : { ...iconBox, background: game.tint, color: game.color }}>
                  {typeof Icon === "string" ? <span style={iconText}>{Icon}</span> : <Icon size={24} />}
                </span>
                <span style={marketClosed ? disabledGameName : gameName}>{game.title}</span>
                <ChevronRight size={18} color={marketClosed ? "#cbd5e1" : "#c7ced8"} />
              </button>
            );
          })}
        </section>
      </main>
    </div>
  );
}

export default Bet;

const page = {
  minHeight: "100vh",
  background: "#f4f7fb",
  color: "#111827",
  fontFamily: "Inter, Arial, sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const topBar = {
  width: "100%",
  maxWidth: 540,
  height: 58,
  position: "fixed",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 40,
  background: "linear-gradient(180deg, #fffdf8 0%, #fff7ed 100%)",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "0 12px",
  borderBottom: "1px solid #fde7c2",
  boxShadow: "0 4px 14px rgba(146, 64, 14, 0.08)",
  flexShrink: 0,
  boxSizing: "border-box",
};

const topBarDesktop = {
  maxWidth: "100%",
  left: 0,
  right: 0,
  transform: "none",
  height: 72,
  padding: "0 max(24px, calc((100vw - 1440px) / 2))",
};

const backButton = {
  width: 36,
  height: 36,
  border: 0,
  background: "transparent",
  display: "grid",
  placeItems: "center",
  color: "#1f2937",
  cursor: "pointer",
};

const brand = {
  color: "#92400e",
  fontSize: 22,
  lineHeight: 1,
  fontWeight: 900,
  minWidth: 0,
  overflow: "hidden",
};

const main = {
  width: "100%",
  maxWidth: 540,
  flex: 1,
  overflow: "auto",
  padding: "112px 10px 18px",
  boxSizing: "border-box",
};

const mainDesktop = {
  maxWidth: 1440,
  padding: "140px 24px 34px",
  overflow: "visible",
};

const marketHeader = {
  minHeight: 54,
  width: "100%",
  maxWidth: 540,
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  top: 58,
  zIndex: 35,
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 10px",
  boxSizing: "border-box",
};

const marketHeaderDesktop = {
  maxWidth: 1440,
  left: "50%",
  transform: "translateX(-50%)",
  top: 72,
  minHeight: 68,
  padding: "12px 24px",
  borderRadius: "0 0 22px 22px",
  boxShadow: "0 10px 28px rgba(15,23,42,0.08)",
};

const marketInfoBlock = {
  display: "grid",
  gap: 2,
  minWidth: 0,
};

const marketTitle = {
  color: "#1f2937",
  fontSize: 13,
  lineHeight: 1.2,
  fontWeight: 900,
  textTransform: "uppercase",
};

const timeText = {
  color: "#94a3b8",
  fontSize: 10,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

const openBadge = {
  minHeight: 22,
  padding: "0 8px",
  borderRadius: 8,
  background: "#dcfce7",
  color: "#15803d",
  fontSize: 9,
  fontWeight: 900,
  display: "inline-flex",
  alignItems: "center",
};

const closedBadge = {
  ...openBadge,
  background: "#f1f5f9",
  color: "#64748b",
};

const gameGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 9,
  minHeight: "calc(100dvh - 130px)",
  alignContent: "stretch",
};

const gameGridDesktop = {
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
  minHeight: "auto",
};

const gameCard = {
  minHeight: 68,
  border: "1px solid #edf1f6",
  borderBottom: "3px solid",
  borderRadius: 10,
  background: "#fff",
  padding: "9px 8px",
  display: "grid",
  gridTemplateColumns: "36px minmax(0, 1fr) 12px",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  textAlign: "left",
};

const gameCardDesktop = {
  minHeight: 112,
  borderRadius: 18,
  padding: "18px 16px",
  gridTemplateColumns: "56px minmax(0, 1fr) 18px",
  gap: 14,
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const disabledGameCard = {
  ...gameCard,
  background: "#f8fafc",
  borderColor: "#e5e7eb",
};

const iconBox = {
  width: 34,
  height: 34,
  borderRadius: 9,
  display: "grid",
  placeItems: "center",
  fontWeight: 900,
};

const disabledIconBox = {
  ...iconBox,
  background: "#e5e7eb",
  color: "#94a3b8",
};

const iconText = {
  fontSize: 13,
  fontWeight: 900,
};

const gameName = {
  fontSize: 11,
  lineHeight: 1.2,
  fontWeight: 700,
};

const disabledGameName = {
  ...gameName,
  color: "#94a3b8",
};
